import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Send } from "lucide-react";

// UI Components
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { useToast } from "../hooks/use-toast";

// Mock Data
import { companyInfo } from "../data/mock";

// Replace with your actual Google Apps Script Web App URL
const GOOGLE_SCRIPT_URL =
  "https://script.google.com/macros/s/AKfycbwy18P6DysHccMoQiOifyvrBkdTsPB_oQYYRbuutFFqZJd1spR0kIrFVVsZ0ZNIw_qOUw/exec";

const Contact = () => {
  const location = useLocation();
  const { toast } = useToast();

  const [services, setServices] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
  });

  useEffect(() => {
    // Scroll to top on mount
    window.scrollTo(0, 0);

    // 1. Fetch services directly from mock data
    import("../data/mock").then((module) => {
      const servicesArray = Object.values(module.services);
      setServices(servicesArray);

      // 2. Check for "?service=slug" in the URL
      const searchParams = new URLSearchParams(location.search);
      const serviceSlug = searchParams.get("service");

      if (serviceSlug) {
        // Find the matching service by its slug to get its full title
        const preSelectedService = servicesArray.find(
          (s) => s.slug === serviceSlug,
        );
        if (preSelectedService) {
          setFormData((prev) => ({
            ...prev,
            service: preSelectedService.title, // Pre-fill the dropdown with the title
          }));
        }
      }
    });
  }, [location.search]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Map contact form fields to match the existing Google Sheet columns
      const payload = {
        formType: "Contact Inquiry", // Identifies this as a Contact form submission
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        position: formData.service, // Maps "Service" to the "Position" column in Sheets
        cover_letter: formData.message, // Maps "Message" to the "Cover Letter" column in Sheets
      };

      const response = await fetch(GOOGLE_SCRIPT_URL, {
        method: "POST",
        headers: { "Content-Type": "text/plain;charset=utf-8" },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        toast({
          title: "Message Sent Successfully!",
          description:
            "Thank you for contacting Maroons. Our team will get back to you shortly.",
          className: "bg-green-50 border-green-200 text-green-900",
        });

        // Reset form
        setFormData({
          name: "",
          email: "",
          phone: "",
          service: "",
          message: "",
        });
      } else {
        throw new Error("Network response was not ok");
      }
    } catch (error) {
      toast({
        title: "Submission Failed",
        description:
          "There was a problem sending your message. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 pt-20 font-sans">
      {/* --- HERO SECTION --- */}
      <section className="relative py-20 px-6 md:px-12 lg:px-24 bg-white overflow-hidden border-b border-slate-100">
        <div className="absolute top-0 left-0 w-full h-full opacity-[0.02] pointer-events-none">
          <svg width="100%" height="100%">
            <pattern
              id="grid"
              width="40"
              height="40"
              patternUnits="userSpaceOnUse"
            >
              <path
                d="M 40 0 L 0 0 0 40"
                fill="none"
                stroke="currentColor"
                strokeWidth="1"
              />
            </pattern>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>

        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-block px-5 py-2 mb-6 text-xs font-black tracking-widest text-maroon-700 uppercase bg-maroon-50 rounded-full"
          >
            Let's Connect
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-5xl md:text-7xl font-extrabold text-slate-900 mb-6 tracking-tight"
          >
            Get In <span className="text-maroon-700">Touch</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-xl text-slate-600 leading-relaxed max-w-2xl mx-auto"
          >
            We consider it an honor to assist you. Share your requirements, and
            our strategic team will respond promptly with tailored solutions.
          </motion.p>
        </div>
      </section>

      {/* --- CONTACT SECTION --- */}
      <section className="py-24 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-5 gap-16">
            {/* Left Column: Contact Form (Takes up 3 cols) */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="lg:col-span-3"
            >
              <Card className="border-slate-100 shadow-2xl rounded-[2rem] overflow-hidden">
                <CardContent className="p-8 md:p-12">
                  <h2 className="text-3xl font-black text-slate-900 mb-2">
                    Send Us a Message
                  </h2>
                  <p className="text-slate-500 mb-8">
                    Fill out the form below and we'll get back to you within 24
                    hours.
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label
                          htmlFor="name"
                          className="text-slate-700 font-bold"
                        >
                          Full Name <span className="text-maroon-600">*</span>
                        </Label>
                        <Input
                          id="name"
                          name="name"
                          type="text"
                          required
                          value={formData.name}
                          onChange={handleChange}
                          className="bg-slate-50 border-slate-200 focus:border-maroon-600 focus:ring-maroon-600 rounded-xl px-4 py-6"
                          placeholder="e.g. John Doe"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label
                          htmlFor="email"
                          className="text-slate-700 font-bold"
                        >
                          Email Address{" "}
                          <span className="text-maroon-600">*</span>
                        </Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          className="bg-slate-50 border-slate-200 focus:border-maroon-600 focus:ring-maroon-600 rounded-xl px-4 py-6"
                          placeholder="e.g. john@company.com"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label
                          htmlFor="phone"
                          className="text-slate-700 font-bold"
                        >
                          Phone Number
                        </Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          className="bg-slate-50 border-slate-200 focus:border-maroon-600 focus:ring-maroon-600 rounded-xl px-4 py-6"
                          placeholder="+91 XXXXX XXXXX"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label
                          htmlFor="service"
                          className="text-slate-700 font-bold"
                        >
                          Service Interested In
                        </Label>
                        <select
                          id="service"
                          name="service"
                          value={formData.service}
                          onChange={handleChange}
                          className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 h-[50px] text-slate-700 focus:outline-none focus:ring-2 focus:ring-maroon-600 focus:border-transparent transition-all"
                        >
                          <option value="">Select a service</option>
                          {services.map((service) => (
                            <option key={service.id} value={service.title}>
                              {service.title}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label
                        htmlFor="message"
                        className="text-slate-700 font-bold"
                      >
                        Message <span className="text-maroon-600">*</span>
                      </Label>
                      <Textarea
                        id="message"
                        name="message"
                        required
                        rows={6}
                        value={formData.message}
                        onChange={handleChange}
                        className="bg-slate-50 border-slate-200 focus:border-maroon-600 focus:ring-maroon-600 resize-none rounded-xl p-4"
                        placeholder="Tell us about your project, goals, or inquiry..."
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-maroon-700 hover:bg-maroon-800 text-white py-7 text-lg rounded-xl font-bold shadow-lg shadow-maroon-200 transition-all transform active:scale-95"
                    >
                      {isSubmitting ? (
                        "Sending Message..."
                      ) : (
                        <>
                          Send Message
                          <Send className="ml-2 h-5 w-5" />
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </motion.div>

            {/* Right Column: Contact Info (Takes up 2 cols) */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
              className="lg:col-span-2 space-y-6"
            >
              <div>
                <h2 className="text-3xl font-black text-slate-900 mb-4">
                  Contact Info
                </h2>
                <p className="text-slate-500 mb-8">
                  Prefer to reach out directly? Use any of the channels below.
                </p>
              </div>

              {/* Address Card */}
              <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-md hover:shadow-lg transition-shadow flex items-start space-x-4 group">
                <div className="h-14 w-14 bg-maroon-50 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-maroon-700 transition-colors">
                  <MapPin className="h-6 w-6 text-maroon-700 group-hover:text-white transition-colors" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-900 mb-1">
                    Corporate Office
                  </h3>
                  <p className="text-slate-500 text-sm leading-relaxed">
                    {companyInfo?.address ||
                      "123 Business Avenue, Tech District"}
                  </p>
                </div>
              </div>

              {/* Phone Card */}
              <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-md hover:shadow-lg transition-shadow flex items-start space-x-4 group">
                <div className="h-14 w-14 bg-maroon-50 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-maroon-700 transition-colors">
                  <Phone className="h-6 w-6 text-maroon-700 group-hover:text-white transition-colors" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-slate-900 mb-2">
                    Direct Lines
                  </h3>
                  <div className="space-y-1">
                    <a
                      href={`tel:${companyInfo?.phone}`}
                      className="block text-slate-500 text-sm hover:text-maroon-700 transition-colors font-medium"
                    >
                      {companyInfo?.phone || "+91 98765 43210"}
                    </a>
                    {companyInfo?.phone2 && (
                      <a
                        href={`tel:${companyInfo.phone2}`}
                        className="block text-slate-500 text-sm hover:text-maroon-700 transition-colors font-medium"
                      >
                        {companyInfo.phone2}
                      </a>
                    )}
                  </div>
                </div>
              </div>

              {/* Email Card */}
              <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-md hover:shadow-lg transition-shadow flex items-start space-x-4 group">
                <div className="h-14 w-14 bg-maroon-50 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-maroon-700 transition-colors">
                  <Mail className="h-6 w-6 text-maroon-700 group-hover:text-white transition-colors" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-900 mb-1">Email Us</h3>
                  <a
                    href={`mailto:${companyInfo?.email}`}
                    className="text-slate-500 text-sm hover:text-maroon-700 transition-colors font-medium"
                  >
                    {companyInfo?.email || "contact@maroons.com"}
                  </a>
                </div>
              </div>

              {/* Business Hours (Highlighted) */}
              <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-8 text-white mt-8 shadow-2xl relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2 blur-2xl"></div>
                <h3 className="text-xl font-bold mb-6">Business Hours</h3>
                <div className="space-y-3 text-slate-300 text-sm font-medium">
                  <div className="flex justify-between items-center border-b border-white/10 pb-2">
                    <span>Monday - Friday</span>
                    <span className="text-white">9:00 AM - 6:00 PM</span>
                  </div>
                  <div className="flex justify-between items-center border-b border-white/10 pb-2">
                    <span>Saturday</span>
                    <span className="text-white">10:00 AM - 4:00 PM</span>
                  </div>
                  <div className="flex justify-between items-center pt-1 text-maroon-300">
                    <span>Sunday</span>
                    <span>Closed</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
